import { SVGProps } from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';

import {
  EpisodeDetailsActiveView,
  useEpisodeDetailsContext,
} from '@insights_module/context/EpisodeDetailsContext';
import { ShieldIcon } from '@icons/ShieldIcon';
import { ChaptersIcon } from '@icons/ChaptersIcon';
import { TriangleCircleSquareXIcon } from '@icons/TriangleCircleSquareXIcon';
import { CheckListIcon } from '@icons/CheckListIcon';
import { SpeakersIcon } from '@icons/SpeakersIcon';
import { TopicsIcon } from '@icons/TopicsIcon';
import { KeywordsIcon } from '@icons/KeywordsIcon';
import { SocialIcon } from '@icons/SocialIcon';
import { ShowNotesIcon } from '@icons/ShowNotesIcon';
import { SummariesIcon } from '@icons/SummariesIcon';
import { ArticleIcon } from '@icons/ArticleIcon';
import { FEATURE_FLAG, useFeatureFlag } from '@auth_module/hooks/useFeatureFlag';
import { useSelectedEpisodeDetails } from '@insights_module/hooks/useGraphqlAudioDetails';

const SidebarContainer = tw.div`min-w-[238px] flex flex-col self-stretch p-[12px] pl-[24px] gap-[8px] bg-page-background border-r border-gray-3`;
const SectionWrapper = tw.div`flex flex-col self-stretch`;
const SectionTitle = tw.h3`text-[14px] text-text-and-icon-muted text-left font-bold p-[8px]`;
const SectionItem = tw.button`flex items-center gap-[8px] py-[8px] px-[16px] text-[14px] text-text-and-icon-muted stroke-text-and-icon-muted cursor-pointer font-medium`;

type EpisodeDetailsSidebarItemsType = Record<
  EpisodeDetailsActiveView,
  { icon: (props: SVGProps<SVGSVGElement>) => JSX.Element }
>;

const INSIGHTS_ITEMS_MAPPER = {
  garm_brand_safety: { icon: ShieldIcon },
  iab_categories: { icon: TriangleCircleSquareXIcon },
  entities: { icon: CheckListIcon },
  speakers: { icon: SpeakersIcon },
} as EpisodeDetailsSidebarItemsType;

const DETAILS_ITEMS_MAPPER = {
  chapters: { icon: ChaptersIcon },
  topics: { icon: TopicsIcon },
  keywords: { icon: KeywordsIcon },
} as EpisodeDetailsSidebarItemsType;

const PROMOTION_ITEMS_MAPPER = {
  social_posts: { icon: SocialIcon },
  show_notes: { icon: ShowNotesIcon },
  summary: { icon: SummariesIcon },
  article: { icon: ArticleIcon },
} as EpisodeDetailsSidebarItemsType;

export const EpisodeDetailsChangeViewSidebar = () => {
  const { isEnabled: isEpisodePromotionEnabled } = useFeatureFlag(
    FEATURE_FLAG.EPISODE_DETAILS_PROMOTION_SECTION
  );
  const { t } = useTranslation();
  const {
    activeView,
    setActiveView,
    setSelectedContextualCategory,
    setSelectedGarmCategory,
    setSelectedEntity,
    setSelectedGarmLevel,
  } = useEpisodeDetailsContext();

  const { data: selectedEpisodeDetails } = useSelectedEpisodeDetails();
  const language = selectedEpisodeDetails?.language;

  // TODO: we should track values for language property and see is 'en' consistent for english episodes
  const isEnglish = language === 'en';

  const handleChangeView = (view: EpisodeDetailsActiveView) => {
    // Clear context values
    setSelectedEntity(null);
    setSelectedGarmLevel(null);
    setSelectedGarmCategory(null);
    setSelectedContextualCategory(null);

    setActiveView(view);
  };

  return (
    <SidebarContainer>
      <SectionWrapper>
        <SectionTitle>
          {t('insights.episode_details_drawer.change_view_sidebar.insights_section_title')}
        </SectionTitle>
        {Object.keys(INSIGHTS_ITEMS_MAPPER).map((key) => {
          const activeViewKey = key as EpisodeDetailsActiveView;
          const { icon: IconComponent } = INSIGHTS_ITEMS_MAPPER[activeViewKey];

          return (
            <SectionItem
              disabled={!isEnglish && activeViewKey === 'entities'}
              key={activeViewKey}
              css={[
                activeView === activeViewKey && tw`text-accent-1 underline stroke-accent-1`,
                !isEnglish && activeViewKey === 'entities' && tw`opacity-50 cursor-auto`,
              ]}
              onClick={() => handleChangeView(activeViewKey)}>
              <IconComponent />
              {t(`insights.episode_details_drawer.change_view_sidebar.${activeViewKey}`)}
            </SectionItem>
          );
        })}
      </SectionWrapper>

      <SectionWrapper>
        <SectionTitle>
          {t('insights.episode_details_drawer.change_view_sidebar.details_section_title')}
        </SectionTitle>
        {Object.keys(DETAILS_ITEMS_MAPPER).map((key) => {
          const activeViewKey = key as EpisodeDetailsActiveView;
          const { icon: IconComponent } = DETAILS_ITEMS_MAPPER[activeViewKey];

          return (
            <SectionItem
              disabled={!isEnglish && activeViewKey !== 'chapters'}
              key={activeViewKey}
              css={[
                activeView === activeViewKey && tw`text-accent-1 underline stroke-accent-1`,
                !isEnglish && activeViewKey !== 'chapters' && tw`opacity-50 cursor-auto`,
              ]}
              onClick={() => handleChangeView(activeViewKey)}>
              <IconComponent />
              {t(`insights.episode_details_drawer.change_view_sidebar.${activeViewKey}`)}
            </SectionItem>
          );
        })}
      </SectionWrapper>

      {isEpisodePromotionEnabled && (
        <SectionWrapper>
          <SectionTitle>
            {t('insights.episode_details_drawer.change_view_sidebar.promotion_section_title')}
          </SectionTitle>
          {Object.keys(PROMOTION_ITEMS_MAPPER).map((key) => {
            const activeViewKey = key as EpisodeDetailsActiveView;
            const { icon: IconComponent } = PROMOTION_ITEMS_MAPPER[activeViewKey];

            return (
              <SectionItem
                disabled={!isEnglish}
                key={activeViewKey}
                css={[
                  activeView === activeViewKey && tw`text-accent-1 underline stroke-accent-1`,
                  !isEnglish && tw`opacity-50 cursor-auto`,
                ]}
                onClick={() => handleChangeView(activeViewKey)}>
                <IconComponent />
                {t(`insights.episode_details_drawer.change_view_sidebar.${activeViewKey}`)}
              </SectionItem>
            );
          })}
        </SectionWrapper>
      )}
    </SidebarContainer>
  );
};
